import * as React from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { withRouter, RouteComponentProps } from 'react-router';
import { compose } from 'redux';
import { Drawer, Notification } from 'components/';
import * as Queries from 'api/graphql/queries';
import { adminUserList, OrderDirection } from '@api/graphql/types';
import GraphQLClient from '@api/graphql/GraphQLClient';
import ControlledTable from '@components/ControlledTable';
import { RowInfo } from 'react-table';
import { Column } from 'react-virtualized';
import { Paths } from '..';
import * as Mutations from '@api/graphql/mutations';
import DeleteButton from '@components/DeleteButton';
import { DispatchProp, connect } from 'react-redux';
import { ListOptionType } from '@reducers/listReducer';
import { ApplicationState } from 'reducers/';
import { OpenInNewCell } from '@components/Table/Cell';

interface StateType {
    data: any[];
    paginatorInfo: any;
}

export interface UserFilterType {
    id: string;
    ext_id: string;
    name: string;
    email: string;
}

const AdminUserListDefaultOptions = {
    sortBy: {
        field: 'id',
        direction: OrderDirection.DESC,
    },
    count: 10,
    page: 0,
    filters: {
        id: '',
        name: '',
        email: '',
    },
};

const listName = 'adminUserList';

interface ReduxProps {
    listOptions: ListOptionType;
}

type Props = ReduxProps & DispatchProp & InjectedIntlProps & RouteComponentProps;

class AdminUserList extends React.Component<Props, StateType> {

    public state = {
        data: [],
        paginatorInfo: {},
    };

    private ActionCell = (row : any) => {
        if (row.original.id) {
            return (
                <DeleteButton
                    className="btn-xs"
                    value={row.original.id}
                    onClick={this.revokeAdmin}
                >
                    {this.props.intl.formatMessage({id: 'module.adminUser.revokeAdmin.label'})}
                </DeleteButton>
            );
        }
        return '';
    }

    private OpenCell = OpenInNewCell((rowInfo: any) => {
        return `${Paths.adminUser}/${rowInfo.original.id}`;
    });

    private tableColumns = [
        {Header: '', sortable: false, Cell: this.OpenCell, width: 30},
        this.getDefaultColumnByName('id', {sortable: true}),
        this.getDefaultColumnByName('name', {sortable: true}),
        this.getDefaultColumnByName('email', {sortable: true}),
        this.getDefaultColumnByName('ip_address'),
        this.getDefaultColumnByName('is_admin', {Cell: this.ActionCell}),
    ];

    public componentDidMount() {
        this.fetchData();
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.listOptions !== this.props.listOptions) {
            this.fetchData();
        }
    }

    private getDefaultColumnByName(name: string, options: any = {}) {
        return {
            Header: this.label(`${name}.title`),
            accessor: name,
            sortable: false,
            ...options,
        };
    }

    private revokeAdmin = (id: string) => {
        GraphQLClient.mutate({
            mutation: Mutations.revokeAdminRightFromUser,
            variables: {id},
        }).then(() => {
            this.fetchData();
            return Notification.success(this.props.intl.formatMessage({ id: 'notification.succeed' }));
        }).catch((e: any) => {
            Notification.error(this.props.intl.formatMessage({ id: 'notification.failed' }));
        });
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.adminUser.${id}` });
    }

    private getFilters() {
        const result = {...this.props.listOptions.filters};
        Object.keys(result).forEach((key) => !result[key] && delete result[key]);
        return result;
    }

    private getSortBy() {
        return {
            field: this.props.listOptions.sortBy.field.toUpperCase(),
            direction: this.props.listOptions.sortBy.direction.toUpperCase(),
        };
    }

    private fetchData() {
        const variables = {...this.props.listOptions, page: this.props.listOptions.page + 1, sortBy: this.getSortBy(), filters: this.getFilters()};
        GraphQLClient.query<adminUserList>({
            query: Queries.adminUserList,
            variables,
        }).then(response => {
            const data = response.data.listAdmins ? response.data.listAdmins.data : [];
            const paginatorInfo = response.data.listAdmins ? response.data.listAdmins.paginatorInfo : {};
            this.setState({data, paginatorInfo});
        });
    }

    private onRowClick = (finalState: any, rowInfo?: RowInfo, column?: Column, instance?: any): object | undefined => {
        if (rowInfo) {
            return {
                onClick: () => {
                    this.props.history.push(`${Paths.adminUser}/${rowInfo.original.id}`);
                },
                style: {
                    cursor: 'pointer',
                },
            };
        }
        return {};
    }


    public render() {
        const addButton = (
            <button className="btn btn-primary" onClick={(e: React.SyntheticEvent) => this.props.history.push(Paths.adminUserCreate)}>
                {this.props.intl.formatMessage({id: 'module.adminUser.create.title'})}
            </button>
        );

        return (
            <Drawer headerButton={addButton}>
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-content">
                                <ControlledTable
                                    listName={listName}
                                    defaultOptions={AdminUserListDefaultOptions}
                                    columns={this.tableColumns}
                                    options={this.props.listOptions}
                                    data={this.state.data}
                                    paginatorInfo={this.state.paginatorInfo}
                                    onRowClick={this.onRowClick}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Drawer>
        );
    }
}

const mapStateToProps = ({ lists }: ApplicationState): ReduxProps => {
    return { listOptions: (lists ? (lists[listName] || AdminUserListDefaultOptions) : AdminUserListDefaultOptions) };
};

export default compose(
    withRouter,
    injectIntl,
    connect(mapStateToProps),
)(AdminUserList);



// WEBPACK FOOTER //
// ./src/modules/AdminUser/pages/AdminUserList.tsx